import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  getAreaColumn,
  MemberNameCell,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { RowViewLink } from '@/components/common/RowActions';
import { formatDisplayDate, formatTime } from '@/utils/date';
import type { SurveyQuestionDetail, SurveySubmissionListItem } from '../types/survey.types';

export const SURVEY_SUBMISSION_NAME_COLUMN_ID = 'userName';

function getAnswerColumn(question: SurveyQuestionDetail): ColumnDef<SurveySubmissionListItem> {
  return {
    id: `question-${question.id}`,
    header: () => <WrappingCellText width="wide">{question.text}</WrappingCellText>,
    cell: ({ row }) => {
      const answer = row.original.answers[String(question.id)];
      return answer ? (
        <WrappingCellText width="wide">
          <ExpandableComment comment={answer} shouldKeepLineBreaks />
        </WrappingCellText>
      ) : (
        <EmptyValue />
      );
    },
  };
}

export function getSurveySubmissionColumns(
  surveyId: number,
  questions: SurveyQuestionDetail[],
  searchText: string | undefined,
): ColumnDef<SurveySubmissionListItem>[] {
  return [
    createSerialColumn<SurveySubmissionListItem>(),
    {
      id: SURVEY_SUBMISSION_NAME_COLUMN_ID,
      header: 'Respondent',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: row.original.userName,
            hrEmpId: row.original.userHrId,
            mobile: row.original.mobile,
          }}
          searchText={searchText}
        />
      ),
    },
    getAreaColumn<SurveySubmissionListItem>(),
    {
      id: 'department',
      header: 'Department',
      cell: ({ row }) => (
        <WrappingCellText>{row.original.departmentName ?? <EmptyValue />}</WrappingCellText>
      ),
    },
    {
      id: 'reportingTo',
      header: 'Reporting To',
      cell: ({ row }) => (
        <WrappingCellText>{row.original.reportingToName ?? <EmptyValue />}</WrappingCellText>
      ),
    },
    {
      id: 'responseDate',
      header: 'Response Date',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.submissionDate)}
          secondary={formatTime(row.original.submittedAt)}
        />
      ),
    },
    ...questions.map(getAnswerColumn),
    createActionsColumn<SurveySubmissionListItem>((submission) => (
      <RowViewLink
        to="/feedback/surveys/$surveyId/submissions/$submissionId"
        params={{ surveyId, submissionId: submission.id }}
        aria-label={`View ${submission.userName}'s submission`}
      />
    )),
  ];
}
